import React from 'react'
import classnames from 'classnames'
import {
	KeypadItem,
	KeypadEntryStatus,
	ScreenNavigationParams,
	AppMetaData,
	RootStoreState,
	CouponCode,
	validateCode as validateCodeAction,
} from 'mytours-read-common'
import { CollectionDetailsState } from 'mytours-read-common/src/collectionlist/reducer'
import { IonGrid, IonRow, IonCol, withIonLifeCycle } from '@ionic/react'
import { Button, Item, Label, List, Spinner, Thumbnail, Input } from '@mytours/components'

import strings from '../../../../strings'

import './keypad.less'
import { getDeviceUUID, getDirMode, getPlatform } from 'mobile_web/modules/platform'
import {
	GetCollectionItemsIndexResponseData,
	GetCollectionsShowResponse,
	GetScreensShowResponse,
	RedeemCode,
} from 'mytours-read-api-typescript'
import { validateCollectionCode } from 'mobile_web/modules/lockapp/functions'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import { CollectionItemsSlug } from 'mobile_web/modules/navigation/routes'
import { ImageSizePreset } from 'common_lib/types'
import { generateImageSrcSet, generateImageUrl } from 'common_lib/utils'
import { useDispatch, useSelector } from 'react-redux'

interface OwnProps {
	keypadEntryStatus?: KeypadEntryStatus
	loadItemOnKeypadEntry: (keyNumber: number) => void
	items: KeypadItem[]
	cleanItems: () => void
	loading: boolean
	forwardedRef?: React.Ref<Keypad>
}

interface Props extends OwnProps {
	appMetaData: AppMetaData
	collections: Map<number, CollectionDetailsState>
	validateCode: (couponCode: CouponCode, linkedId: number) => Promise<boolean>
}

interface State {
	valueArr: number[]
	errorMessage: string
	keypadValue: number
}

const SCREEN_ITEM_SUBTYPES: Partial<
	Record<NonNullable<KeypadItem['subtype']>, GetCollectionItemsIndexResponseData.ItemSubtypeEnum>
> = {
	[GetScreensShowResponse.ScreenTypeEnum.Panorama]: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama,
	[GetScreensShowResponse.ScreenTypeEnum.Story]: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Story,
	[GetScreensShowResponse.ScreenTypeEnum.Web]: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web,
}

const COLLECTION_SLUG_BY_SUBTYPE: Partial<Record<GetCollectionsShowResponse.SubtypeEnum, CollectionItemsSlug>> = {
	[GetCollectionsShowResponse.SubtypeEnum.CollectionTour]: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour,
	[GetCollectionsShowResponse.SubtypeEnum.CollectionList]: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List,
	[GetCollectionsShowResponse.SubtypeEnum.CollectionMenu]: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu,
}

class Keypad extends React.Component<Props, State> {
	state = {
		valueArr: [],
		errorMessage: '',
		keypadValue: -1,
	}

	getMaxLength = (): number => {
		const { appMetaData } = this.props
		if (!appMetaData) {
			return
		}
		const maxLength = appMetaData.keypad_digits
		return maxLength
	}

	componentDidMount = () => {
		const { cleanItems } = this.props
		cleanItems()
	}

	ionViewDidLeave() {
		this.cleanItems()
	}

	cleanItems = () => {
		const { cleanItems } = this.props

		cleanItems()
		this.setState({
			valueArr: [],
			keypadValue: -1,
		})
	}

	numberToString = (value: number): string => {
		const isNumber = typeof value === 'number' && !Number.isNaN(value)
		return (isNumber && value.toString()) || ''
	}

	renderButton = (value: number): JSX.Element => (
		<Button
			variant="primary"
			fill="var(--keypad-background-color, var(--button-bg))"
			className="keypad-button"
			onClick={() => this.updateValue(value)}
			aria-label={this.numberToString(value)}
		>
			{value}
		</Button>
	)

	updateValue = (value: number): void => {
		const { valueArr } = this.state

		if (this.getMaxLength() <= valueArr.length) {
			return
		}

		const newValueArray = [...this.state.valueArr, value]

		this.setState({ valueArr: newValueArray, keypadValue: this.getValue(newValueArray) })
	}

	getValue = (valueArr?: number[]): number => {
		if (!valueArr) {
			valueArr = this.state.valueArr
		}

		const value = valueArr.length ? Number(valueArr.join('')) : -1
		return value
	}

	removeValue = (): void => {
		const valueArr = [...this.state.valueArr]
		valueArr.pop()

		this.setState({
			valueArr,
			keypadValue: this.getValue(valueArr),
		})
	}

	onSubmit = (): void => {
		this.props.loadItemOnKeypadEntry(this.state.keypadValue)
	}

	navigateToItem = async ({ id: itemId, title, version: itemVersion, subtype, collectionIds }: KeypadItem) => {
		const { collections } = this.props

		this.cleanItems()

		if (collectionIds && !(await this.hasAccessToAnyCollection(collectionIds))) {
			const parentCollection = collections.get(collectionIds[0])?.details
			if (!parentCollection) {
				return
			}

			NavigationService.navigateToItem({
				itemId: parentCollection.id,
				itemVersion: parentCollection.version,
				type: parentCollection.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionTour ? 'tour' : 'list',
				title,
			})
			return
		}

		// A screen that belongs to a collection must open within it, otherwise the
		// collection navigation is lost — for tours the header (hamburger menu) and
		// footer (next/previous), for lists/menus the next/previous links.
		const screenItemSubtype = subtype && SCREEN_ITEM_SUBTYPES[subtype]
		const parentCollection = screenItemSubtype ? this.findParentCollection(collectionIds) : undefined
		const collectionSlug = parentCollection?.subtype && COLLECTION_SLUG_BY_SUBTYPE[parentCollection.subtype]
		if (parentCollection && collectionSlug && screenItemSubtype) {
			NavigationService.navigateToCollectionItems({
				params: {
					collectionId: parentCollection.id,
					collectionVersion: parentCollection.version,
					itemId,
					collectionTitle: parentCollection.title ?? '',
					title: parentCollection.title ?? '',
				},
				collectionType: collectionSlug,
				itemType: screenItemSubtype,
			})
			return
		}

		const screenParams: ScreenNavigationParams = {
			itemId,
			title,
			itemVersion,
		}

		switch (subtype) {
			case GetScreensShowResponse.ScreenTypeEnum.Panorama:
				NavigationService.navigateToScreenPanorama(screenParams)
				break
			case GetScreensShowResponse.ScreenTypeEnum.Story:
				NavigationService.navigateToScreenStory(screenParams)
				break
			case GetScreensShowResponse.ScreenTypeEnum.Web:
				NavigationService.navigateToScreenWeb(screenParams)
				break
			case GetCollectionsShowResponse.SubtypeEnum.CollectionList:
			case GetCollectionsShowResponse.SubtypeEnum.CollectionMenu:
				NavigationService.navigateToItem({
					itemId,
					itemVersion,
					type: 'list',
					title,
				})
				break
			case GetCollectionsShowResponse.SubtypeEnum.CollectionTour:
				NavigationService.navigateToItem({
					itemId,
					itemVersion,
					type: 'tour',
					title,
				})
				break
		}
	}

	findParentCollection = (collectionIds?: number[]) => {
		const { collections } = this.props

		return (collectionIds || [])
			.map(collectionId => collections.get(collectionId)?.details)
			.find(details => details?.subtype && COLLECTION_SLUG_BY_SUBTYPE[details.subtype])
	}

	hasAccessToAnyCollection = async (collectionIds: number[]) => {
		const { collections, validateCode } = this.props

		for (const collectionId of collectionIds) {
			const collection = collections.get(collectionId)?.details
			if (!collection) {
				continue
			}

			if (collection.code_lock || (collection.pricing?.active && !!collection.pricing?.web_price?.amount)) {
				const isValid = await validateCollectionCode(collection, validateCode)
				if (isValid) {
					return true
				}
			} else {
				return true
			}
		}

		return false
	}

	renderItem = (item: KeypadItem): JSX.Element => {
		const { icon } = item

		return (
			<Item key={item.id} onClick={() => this.navigateToItem(item)}>
				<Thumbnail className={!(icon && icon.file) && 'empty-image'} slot="start">
					{icon && icon.file && (
						<img
							src={generateImageUrl(icon.file, { imageSizePreset: ImageSizePreset.Thumbnail })}
							srcSet={generateImageSrcSet(icon.file, { imageSizePreset: ImageSizePreset.Thumbnail })}
							alt={item.title}
						/>
					)}
				</Thumbnail>
				<Label as="span">{item.title}</Label>
			</Item>
		)
	}

	renderResults = (): JSX.Element => {
		const { items, cleanItems } = this.props
		if (!items) {
			return undefined
		}

		const loading = this.props.loading || this.areCollectionsForKeypadItemsLoading()

		return (
			<>
				<IonGrid className="results-list section-group" dir={getDirMode()}>
					<h2 className={classnames('subheading results-subheading', getDirMode())}>{strings.results}</h2>
					{loading ? (
						<Spinner variant="crescent" aria-label={strings.loading} />
					) : (
						<List variant="ios" className={classnames('section-group-list', getDirMode())} lines="full">
							{items.map(this.renderItem)}
						</List>
					)}
				</IonGrid>
				{cleanItems && (
					<Button
						variant="clear"
						className="backspace-button"
						onClick={cleanItems}
						aria-label={strings.accessibility_go_back}
					>
						<i className="action-icon fal fa-long-arrow-left" />
					</Button>
				)}
			</>
		)
	}

	areCollectionsForKeypadItemsLoading = () => {
		const { items, collections } = this.props

		if (!items) {
			return true
		}

		for (const item of items) {
			for (const collectionId of item.collectionIds || []) {
				if (!collections.get(collectionId)?.details) {
					return true
				}
			}
		}

		return false
	}

	render() {
		const { keypadEntryStatus, items } = this.props
		const { valueArr, errorMessage } = this.state
		const errorAlert = keypadEntryStatus === KeypadEntryStatus.Failed ? strings.keypad_entry_error : errorMessage

		return (
			<div className="keypad-container">
				<IonGrid className="keypad-grid">
					<h2 className="keypad-heading">{strings.keypad_entry_heading}</h2>
					<IonRow className="value-row">
						<IonCol size="auto">
							<Item variant="ios" as="div" className="value-item" lines="none">
								{/* Named rather than presentational: the field takes focus, so it stays in the
								    accessibility tree whatever role it claims, and the code typed on the keypad
								    is the one thing here a screen reader has to be able to read back. */}
								<Input
									variant="ios"
									className="value-input"
									value={valueArr.join('')}
									readOnly
									// eslint-disable-next-line jsx-a11y/no-autofocus -- the field took focus on open before the swap too
									autoFocus
									aria-label={strings.keypad_entry_heading}
								/>
							</Item>
						</IonCol>
					</IonRow>
					{items && items.length > 0 ? (
						this.renderResults()
					) : (
						<>
							{errorAlert && (
								<IonRow role="alert" className="error-row">
									{errorAlert}
								</IonRow>
							)}
							<IonRow>
								<IonCol>{this.renderButton(1)}</IonCol>
								<IonCol>{this.renderButton(2)}</IonCol>
								<IonCol>{this.renderButton(3)}</IonCol>
							</IonRow>
							<IonRow>
								<IonCol>{this.renderButton(4)}</IonCol>
								<IonCol>{this.renderButton(5)}</IonCol>
								<IonCol>{this.renderButton(6)}</IonCol>
							</IonRow>
							<IonRow>
								<IonCol>{this.renderButton(7)}</IonCol>
								<IonCol>{this.renderButton(8)}</IonCol>
								<IonCol>{this.renderButton(9)}</IonCol>
							</IonRow>
							<IonRow>
								<IonCol>
									<Button
										variant="clear"
										className="backspace-button"
										onClick={this.removeValue}
										aria-label={strings.accessibility_remove}
									>
										<i className="action-icon fal fa-backspace" />
									</Button>
								</IonCol>
								<IonCol>{this.renderButton(0)}</IonCol>
								<IonCol>
									<Button
										variant="clear"
										className="backspace-button"
										onClick={this.onSubmit}
										aria-label={strings.view_stop}
									>
										<i className="action-icon fal fa-long-arrow-right" />
									</Button>
								</IonCol>
							</IonRow>
						</>
					)}
				</IonGrid>
			</div>
		)
	}
}

function ConnectedKeypad(props: OwnProps) {
	const dispatch = useDispatch()
	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)
	const collections = useSelector((state: RootStoreState) => state.collectionlist.collectionDetails)
	const validateCode = (couponCode: CouponCode, linkedId: number) => {
		const platform = getPlatform()
		const deviceUUID = getDeviceUUID()

		return new Promise<boolean>(res => {
			dispatch(
				validateCodeAction.started({
					linkedId,
					linkedType: RedeemCode.LinkedTypeEnum.Collection,
					devicePlatform: platform,
					deviceUdid: deviceUUID,
					couponCode: couponCode.code,
					expireAt: couponCode.expireAt,
					onComplete: res,
				}),
			)
		})
	}

	return <Keypad appMetaData={appMetaData} collections={collections} validateCode={validateCode} {...props} />
}

const ConnectedKeypadRefTaking = React.forwardRef((props: OwnProps, ref: React.Ref<Keypad>) => (
	<ConnectedKeypad {...props} forwardedRef={ref} />
))
ConnectedKeypadRefTaking.displayName = 'ConnectedKeypadRefTaking'

export default withIonLifeCycle(ConnectedKeypadRefTaking)
